/* eslint-disable @typescript-eslint/ban-ts-comment */
import { Line } from '@react-three/drei';
import { MeasureKind, UnitsByMeasure } from 'convert';
import React, { memo, useMemo } from 'react';
import { useIntl } from 'react-intl';
import * as THREE from 'three';
import { EUnitType } from '../../../@types/Enums';
import { useLanguage } from '../../../provider/LanguageProvider';
import { useUnitContext } from '../../../provider/UnitProvider';
import {
  DEFAULT_INDICATOR_COLOR,
  DEFAULT_INDICATOR_FONT_SIZE_XS,
  DEFAULT_INDICATOR_LINE_WIDTH,
  INNER_WALL_OFFSET_ADJUSTED
} from '../../../utils/constants';
import { convertTo, getLocalizedUnitLabel } from '../../../utils/conversionUtils';
import { localizeNumber } from '../../../utils/numberUtils';
import RENDER_ORDERS from '../../../utils/renderOrder';
import RoundedBoxWithText from '../RoundedBoxWithText';

interface ElementDistanceIndicatorProps {
  windows: any[];
  doors: any[];
  passages?: any[];
  visualStartPoint: THREE.Vector3;
  visualEndPoint: THREE.Vector3;
  inwardNormal: THREE.Vector3;
  color?: string;
  lineWidth?: number;
}

const ElementDistanceIndicator: React.FC<ElementDistanceIndicatorProps> = ({
  windows,
  doors,
  passages = [],
  visualStartPoint,
  visualEndPoint,
  inwardNormal,
  color = DEFAULT_INDICATOR_COLOR,
  lineWidth = DEFAULT_INDICATOR_LINE_WIDTH,
}): React.ReactElement | null => {
  const { unitType } = useUnitContext();
  const { locale } = useLanguage();
  const intl = useIntl();

  const mLocalizedLabel = useMemo(() => getLocalizedUnitLabel(intl, 'meter'), [locale, intl]);
  const ftLocalizedLabel = useMemo(() => getLocalizedUnitLabel(intl, 'foot'), [locale, intl]);

  const windowsSig = windows.map((w) => `${w.uuid ?? ''}:${w.position.x},${w.position.y},${w.length},${w.depth}`).join('|');
  const doorsSig = doors.map((d) => `${d.uuid ?? ''}:${d.position.x},${d.position.y},${d.length},${d.depth}`).join('|');
  const passagesSig = passages.map((p) => `${p.uuid ?? ''}:${p.position.x},${p.position.y},${p.length},${p.depth}`).join('|');

  const distanceIndicators = useMemo(() => {
    const dir = new THREE.Vector3().subVectors(visualEndPoint, visualStartPoint).normalize();
    const totalLength = visualStartPoint.distanceTo(visualEndPoint);

    const elementsData = [
      ...windows.map((w) => ({ position: w.position, width: w.length })),
      ...doors.map((d) => ({ position: d.position, width: d.length })),
      ...passages.map((p) => ({ position: p.position, width: p.length }))
    ];

    if (elementsData.length === 0) return [];

    const sortedElements = elementsData.map((e) => {
      const tCenter = e.position.clone().sub(visualStartPoint).dot(dir);
      return {
        startT: tCenter - e.width / 2,
        endT: tCenter + e.width / 2,
      };
    }).sort((a, b) => a.startT - b.startT);

    const indicators: any[] = [];
    const offsetVec = inwardNormal.clone().multiplyScalar(INNER_WALL_OFFSET_ADJUSTED / 2);
    const baselineStart = visualStartPoint.clone().add(offsetVec);

    const addIndicator = (t0: number, t1: number) => {
      const distance = t1 - t0;
      if (distance <= 0.01) return; 

      const rawStart = visualStartPoint.clone().add(dir.clone().multiplyScalar(t0));
      const rawEnd = visualStartPoint.clone().add(dir.clone().multiplyScalar(t1));

      const drawStart = baselineStart.clone().add(dir.clone().multiplyScalar(t0));
      const drawEnd = baselineStart.clone().add(dir.clone().multiplyScalar(t1));

      const mid = drawStart.clone().lerp(drawEnd, 0.5);
      mid.z = 1;

      let lengthText = '';
      if (unitType === EUnitType.METRIC) {
        lengthText = `${localizeNumber((distance / 10).toFixed(2), locale)}${mLocalizedLabel}`;
      } else {
        lengthText = `${localizeNumber(
          convertTo(distance / 10, 'meters' as UnitsByMeasure<MeasureKind.Length>, 'feet' as UnitsByMeasure<MeasureKind.Length>).toFixed(2),
          locale,
        )}${ftLocalizedLabel}`;
      }

      indicators.push({
        startPoint: drawStart,
        endPoint: drawEnd,
        midPoint: mid,
        lengthText,
        anchorStart: rawStart,
        anchorEnd: rawEnd,
      });
    };

    let currentT = 0;

    sortedElements.forEach((el) => {
      if (el.startT > currentT) {
        addIndicator(currentT, el.startT);
      }
      currentT = Math.max(currentT, el.endT);
    });

    if (currentT < totalLength) {
      addIndicator(currentT, totalLength);
    }

    return indicators;
  }, [
    windowsSig, 
    doorsSig,   
    passagesSig,
    visualStartPoint, 
    visualEndPoint, 
    inwardNormal, 
    unitType, 
    locale, 
    mLocalizedLabel, 
    ftLocalizedLabel
  ]);

  return (
    <group name="group-elementDistanceIndicator" renderOrder={RENDER_ORDERS.lengthIndicator}>
      {distanceIndicators.map((indicator, index) => (
        <React.Fragment key={index}>
          <Line points={[indicator.startPoint.toArray(), indicator.endPoint.toArray()]} color={color} lineWidth={lineWidth} transparent />
          <RoundedBoxWithText position={indicator.midPoint} fontSize={DEFAULT_INDICATOR_FONT_SIZE_XS} text={indicator.lengthText} />
          <Line
            points={[
              [indicator.anchorStart.x, indicator.anchorStart.y, 0],
              [indicator.startPoint.x, indicator.startPoint.y, 0],
            ]}
            color={color}
            lineWidth={lineWidth}
            transparent
          />
          <Line
            points={[
              [indicator.anchorEnd.x, indicator.anchorEnd.y, 0],
              [indicator.endPoint.x, indicator.endPoint.y, 0],
            ]}
            color={color}
            lineWidth={lineWidth}
            transparent
          />
        </React.Fragment>
      ))}
    </group>
  );
};

export default memo(ElementDistanceIndicator);
